export { InsertRootExerciseButton } from "src/components/InsertRootExerciseButton";
export { InsertWorkoutItem } from "src/components/InsertWorkoutItem";
export {
  getAllowedSectionOptions,
  type InsertRootExerciseButtonProps,
  type InsertWorkoutItemProps,
  type ParentContext,
} from "./AddExerciseOrBlock.shared";
import { type WorkoutFormat } from "api-client/legacy-trpc";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "ui";

import {
  getAllowedSectionOptions,
  getParentContext,
  type ParentContext,
} from "./AddExerciseOrBlock.shared";
import { ImportSectionDialog } from "./ImportSectionDialog";
import { SectionFormatIcon } from "./SectionFormatIcon";
import { useInsertWorkoutItem } from "./useInsertWorkoutItem";
import { useWorkoutEditStore } from "./WorkoutEditContext";
import { useWorkoutPermissions } from "./WorkoutItemsPermission";

interface AddExerciseOrBlockProps {
  parentId: string | null;
}

export const AddExerciseOrBlock = observer(function AddExerciseOrBlock(
  props: AddExerciseOrBlockProps,
) {
  const { t } = useTranslation();
  const workoutEditStore = useWorkoutEditStore();
  const permissions = useWorkoutPermissions();
  const insertWorkoutItem = useInsertWorkoutItem();
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const parentContext: ParentContext | null = props.parentId
    ? getParentContext(workoutEditStore.getWorkoutItemById(props.parentId))
    : null;

  const { sections } = getAllowedSectionOptions(parentContext);
  const showSectionMenu = sections.length > 0;

  function handleAddSection(format: WorkoutFormat) {
    insertWorkoutItem(
      workoutEditStore.getNextOrder(props.parentId),
      props.parentId,
      "GROUP",
      { format: format, groupType: "WORKOUT" },
    );
  }

  return (
    <div className={"flex gap-2 w-full"}>
      {showSectionMenu ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className={"flex-1"} variant="outline" size="sm">
              {t("editWorkout.addSectionButton")}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[180px]">
            {permissions.canImportFromLibrary && (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    setImportDialogOpen(true);
                  }}
                  className="gap-2"
                >
                  <Icon name="tabler-section" size={16} />
                  {t("editWorkout.importSection.fromLibrary")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {sections.map((section) => {
              return (
                <DropdownMenuItem
                  key={section.format}
                  onClick={() => {
                    handleAddSection(section.format);
                  }}
                  className="gap-2"
                >
                  <SectionFormatIcon format={section.format} />
                  {t(section.labelKey)}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button
        className={"flex-1"}
        onClick={() => {
          insertWorkoutItem(
            workoutEditStore.getNextOrder(props.parentId),
            props.parentId,
            "NORMAL",
          );
        }}
        variant="outline"
        size="sm"
      >
        {t("editWorkout.addExerciseButton")}
      </Button>
      {permissions.canImportFromLibrary && (
        <ImportSectionDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          insertAt={workoutEditStore.getNextOrder(props.parentId)}
        />
      )}
    </div>
  );
});
